import  { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { blogsService } from '../../services/dataService';
import { formatBlogData, generateSlug } from '../../services/blogHelpers';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, X } from 'lucide-react';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;
  
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      slug: '',
      published: false,
      featured: false
    }
  });

  const title = watch('title');

  // Auto-generate slug from title
  useEffect(() => {
    if (title && !isEditing) {
      setValue('slug', generateSlug(title));
    }
  }, [title, isEditing, setValue]);

  // Load blog data if editing
  useEffect(() => {
    if (isEditing) {
      loadBlog();
    }
  }, [id]);

  const loadBlog = async () => {
    setInitialLoading(true);
    try {
      const result = await blogsService.getById(id);
      if (result.success) {
        const blog = result.data;
        reset({
          title: blog.title,
          description: blog.description || '',
          date: blog.date,
          slug: blog.slug,
          published: blog.published || false,
          featured: blog.featured || false
        });
        setContent(blog.content || '');
        setTags(blog.tags || []);
      } else {
        toast.error('Blog not found');
        navigate('/adminaccess/writings');
      }
    } catch (error) {
      toast.error('Error loading blog');
    } finally {
      setInitialLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };

  const onSubmit = async (data) => {
    if (!content.trim()) {
      toast.error('Please add some content to your blog post');
      return;
    }

    setLoading(true);
    
    const blogData = formatBlogData({
      ...data,
      content,
      tags
    });

    try {
      let result;
      if (isEditing) {
        result = await blogsService.update(id, blogData);
      } else {
        result = await blogsService.create(blogData);
      }

      if (result.success) {
        toast.success(isEditing ? 'Blog updated!' : 'Blog created!');
        navigate('/adminaccess/writings');
      } else {
        toast.error('Failed to save blog');
      }
    } catch (error) {
      toast.error('Error saving blog');
    } finally {
      setLoading(false);
    }
  };

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link', 'image'],
      ['clean']
    ]
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading blog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/writings')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <h1 className="text-lg font-medium">
            {isEditing ? 'Edit Blog Post' : 'New Blog Post'}
          </h1>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-5xl mx-auto px-6 py-12">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          
          {/* Title */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Title *
            </label>
            <input
              type="text"
              {...register('title', { required: 'Title is required' })}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-2xl placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors font-light"
              placeholder="Getting Started with React..."
            />
            {errors.title && (
              <p className="text-red-400 text-xs mt-1">{errors.title.message}</p>
            )}
          </div>

          {/* Slug */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              URL Slug *
            </label>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm">blog.aakashpoudel.com/</span>
              <input
                type="text"
                {...register('slug', { required: 'Slug is required' })}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="getting-started-with-react"
              />
            </div>
            {errors.slug && (
              <p className="text-red-400 text-xs mt-1">{errors.slug.message}</p>
            )}
            <p className="text-white/30 text-xs mt-1">Auto-generated from title (editable)</p>
          </div>

          {/* Description - NOW OPTIONAL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
              placeholder="A brief description of your blog post (optional)..."
            />
            <p className="text-white/30 text-xs mt-1">
              Optional: Add a short description for SEO and previews
            </p>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Content *
            </label>
            <div className="bg-white rounded-lg overflow-hidden">
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                className="quill-editor"
                placeholder="Start writing your blog post..."
              />
            </div>
            <p className="text-white/30 text-xs mt-1">
              Supports rich text, code blocks, images, and links
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Tags
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
                placeholder="e.g. React, Tutorial, JavaScript"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 rounded-lg transition-colors"
              >
                Add
              </button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-lg text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="hover:text-green-200 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Publication Date
            </label>
            <input
              type="date"
              {...register('date')}
              className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-white/30 transition-colors"
            />
          </div>

          {/* Options */}
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('published')}
                className="w-4 h-4 rounded bg-white/5 border border-white/10"
              />
              <span className="text-white/60 text-sm">Publish immediately</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('featured')}
                className="w-4 h-4 rounded bg-white/5 border border-white/10"
              />
              <span className="text-white/60 text-sm">Featured post</span>
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-3 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : isEditing ? 'Update Post' : 'Create Post'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/adminaccess/writings')}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </main>

      {/* Custom Styles for Quill Editor - FIXED */}
      <style jsx global>{`
        .quill-editor {
          min-height: 400px;
        }
        .ql-toolbar {
          background: #f3f4f6;
          border-bottom: 1px solid #e5e7eb !important;
        }
        .ql-container {
          font-family: ui-sans-serif, system-ui, -apple-system;
          font-size: 16px;
          min-height: 400px;
          background: white;
        }
        .ql-editor {
          min-height: 400px;
          color: #1f2937 !important;
        }
        .ql-editor * {
          color: #1f2937 !important;
        }
        .ql-editor h1 {
          color: #111827 !important;
          font-weight: 700;
        }
        .ql-editor h2 {
          color: #111827 !important;
          font-weight: 600;
        }
        .ql-editor h3 {
          color: #111827 !important;
          font-weight: 600;
        }
        .ql-editor code,
        .ql-editor pre {
          background: #f3f4f6 !important;
          color: #1f2937 !important;
        }
        .ql-editor.ql-blank::before {
          color: #9ca3af !important;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default BlogEditor;